import React, { useEffect, useState } from 'react';
import { useParams, Link } from 'react-router-dom';
import { orderAPI, addressAPI } from '../../services/api';
import { Loader, ArrowLeft, Package, Truck, CheckCircle, Clock, XCircle, MapPin } from 'lucide-react';

const OrderTracking = () => {
    const { id } = useParams();
    const [order, setOrder] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [addresses, setAddresses] = useState([]);
    const [addressModalOpen, setAddressModalOpen] = useState(false);
    const [selectedAddressId, setSelectedAddressId] = useState(null);
    const [actionLoading, setActionLoading] = useState(false);

    useEffect(() => {
        fetchOrder();
    }, [id]);

    const fetchOrder = async () => {
        try {
            const response = await orderAPI.getById(id);
            setOrder(response.data);
        } catch (err) {
            console.error("Failed to fetch order", err);
            setError("Failed to load order details");
        } finally {
            setLoading(false);
        }
    };

    const fetchAddresses = async () => {
        try {
            const response = await addressAPI.getAll();
            setAddresses(response.data.data || []);
        } catch (error) {
            console.error("Failed to fetch addresses", error);
        }
    };

    const handleCancelOrder = async () => {
        if (window.confirm("Are you sure you want to cancel this order?")) {
            setActionLoading(true);
            try {
                await orderAPI.cancelOrder(order.id);
                fetchOrder(); // Refresh order details
            } catch (error) {
                console.error("Failed to cancel order", error);
                alert("Failed to cancel order");
            } finally {
                setActionLoading(false);
            }
        }
    };

    const handleChangeAddressClick = () => {
        fetchAddresses();
        setAddressModalOpen(true);
        setSelectedAddressId(order.address_id);
    };

    const handleUpdateAddress = async () => {
        if (!selectedAddressId) return;
        setActionLoading(true);
        try {
            await orderAPI.updateAddress(order.id, selectedAddressId);
            setAddressModalOpen(false);
            fetchOrder(); // Refresh order details
        } catch (error) {
            console.error("Failed to update address", error);
            alert("Failed to update address");
        } finally {
            setActionLoading(false);
        }
    };

    const getStatusStep = (status) => {
        const statuses = ['Pending', 'Confirmed', 'Packed', 'Shipped', 'Delivered'];
        const index = statuses.findIndex(s => s.toLowerCase() === status?.toLowerCase());
        return index >= 0 ? index : 0;
    };

    const statusSteps = [
        { label: 'Pending', icon: Clock },
        { label: 'Confirmed', icon: CheckCircle },
        { label: 'Packed', icon: Package },
        { label: 'Shipped', icon: Truck },
        { label: 'Delivered', icon: CheckCircle }
    ];

    if (loading) {
        return (
            <div className="pt-24 min-h-screen bg-gray-50 flex justify-center items-center">
                <Loader className="animate-spin text-green-600" size={48} />
            </div>
        );
    }

    if (error || !order) {
        return (
            <div className="pt-24 min-h-screen bg-gray-50 px-6">
                <div className="container mx-auto max-w-4xl">
                    <div className="bg-white rounded-xl shadow p-6 text-center">
                        <p className="text-red-500 text-lg">{error || "Order not found"}</p>
                        <Link to="/orders" className="mt-4 inline-block text-green-600 hover:underline">
                            ← Back to Orders
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    const currentStep = getStatusStep(order.status);

    return (
        <div className="pt-24 min-h-screen bg-gray-50 px-6 pb-12">
            <div className="container mx-auto max-w-4xl">
                <Link to="/orders" className="inline-flex items-center text-green-600 hover:text-green-700 mb-6">
                    <ArrowLeft size={20} className="mr-2" />
                    Back to Orders
                </Link>

                <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                    <h1 className="text-2xl font-bold text-gray-800 mb-2">Order #{order.id}</h1>
                    <p className="text-gray-500">Placed on {new Date(order.createdAt).toLocaleDateString()}</p>
                </div>

                {/* Status Tracker */}
                <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-6">Order Status</h2>
                    <div className="flex items-center justify-between">
                        {statusSteps.map((step, index) => {
                            const Icon = step.icon;
                            const isCompleted = index <= currentStep;
                            const isCurrent = index === currentStep;
                            return (
                                <React.Fragment key={step.label}>
                                    <div className="flex flex-col items-center">
                                        <div className={`w-10 h-10 rounded-full flex items-center justify-center ${isCompleted ? 'bg-green-500 text-white' : 'bg-gray-200 text-gray-400'
                                            } ${isCurrent ? 'ring-4 ring-green-200' : ''}`}>
                                            <Icon size={20} />
                                        </div>
                                        <p className={`text-xs mt-2 text-center ${isCompleted ? 'text-green-600 font-medium' : 'text-gray-400'}`}>
                                            {step.label}
                                        </p>
                                    </div>
                                    {index < statusSteps.length - 1 && (
                                        <div className={`flex-1 h-1 mx-2 ${index < currentStep ? 'bg-green-500' : 'bg-gray-200'}`} />
                                    )}
                                </React.Fragment>
                            );
                        })}
                    </div>
                </div>

                {/* Order Items */}
                <div className="bg-white rounded-xl shadow-md p-6 mb-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Order Items</h2>
                    <ul className="divide-y divide-gray-100">
                        {order.items?.map((item, index) => (
                            <li key={index} className="py-4 flex items-center">
                                <div className="h-16 w-16 flex-shrink-0 border border-gray-200 rounded-md overflow-hidden bg-gray-100">
                                    <Package className="h-full w-full p-4 text-gray-400" />
                                </div>
                                <div className="ml-4 flex-1">
                                    <p className="text-sm font-medium text-gray-900">
                                        {item.variation?.product?.name || 'Product'}
                                    </p>
                                    <p className="text-sm text-gray-500">Qty: {item.qty}</p>
                                </div>
                                <div className="text-sm font-medium text-gray-900">
                                    ₹{item.totalPrice || item.price * item.qty}
                                </div>
                            </li>
                        ))}
                    </ul>
                </div>

                {/* Order Summary */}
                <div className="bg-white rounded-xl shadow-md p-6">
                    <h2 className="text-lg font-semibold text-gray-800 mb-4">Order Summary</h2>
                    <div className="space-y-2">
                        <div className="flex justify-between text-gray-600">
                            <span>Shipping Charges</span>
                            <span>₹{order.shippingCharges || 0}</span>
                        </div>
                        <div className="flex justify-between text-lg font-bold text-gray-800 pt-2 border-t">
                            <span>Total</span>
                            <span>₹{order.totalAmount}</span>
                        </div>
                    </div>

                    {order.address && (
                        <div className="mt-6 pt-4 border-t">
                            <h3 className="font-medium text-gray-800 mb-2">Delivery Address</h3>
                            <p className="text-sm text-gray-600">
                                {order.address.name}<br />
                                {order.address.address}, {order.address.village}<br />
                                {order.address.city}, {order.address.district}<br />
                                {order.address.state} - {order.address.pincode}<br />
                                Phone: {order.address.mobile}
                            </p>
                        </div>
                    )}

                    {/* Actions for Pending Orders */}
                    {order.status === 'Pending' && (
                        <div className="mt-6 pt-4 border-t flex flex-wrap gap-4">
                            <button
                                onClick={handleCancelOrder}
                                disabled={actionLoading}
                                className="flex items-center gap-2 px-4 py-2 border border-red-200 text-red-600 rounded-lg hover:bg-red-50 transition"
                            >
                                <XCircle size={18} /> Cancel Order
                            </button>
                            <button
                                onClick={handleChangeAddressClick}
                                disabled={actionLoading}
                                className="flex items-center gap-2 px-4 py-2 border border-green-200 text-green-600 rounded-lg hover:bg-green-50 transition"
                            >
                                <MapPin size={18} /> Change Address
                            </button>
                        </div>
                    )}
                </div>

                {/* Change Address Modal */}
                {addressModalOpen && (
                    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
                        <div className="bg-white rounded-xl shadow-xl max-w-lg w-full max-h-[90vh] overflow-y-auto">
                            <div className="p-6 border-b border-gray-100 flex justify-between items-center">
                                <h2 className="text-xl font-bold text-gray-800">Select New Address</h2>
                                <button onClick={() => setAddressModalOpen(false)} className="text-gray-400 hover:text-gray-600">
                                    &times;
                                </button>
                            </div>
                            <div className="p-6">
                                <div className="space-y-3 mb-6">
                                    {addresses.map((addr) => (
                                        <div key={addr.id} className="flex items-start">
                                            <input
                                                id={`addr-${addr.id}`}
                                                name="address-selection"
                                                type="radio"
                                                checked={selectedAddressId === addr.id}
                                                onChange={() => setSelectedAddressId(addr.id)}
                                                className="focus:ring-green-500 h-4 w-4 text-green-600 border-gray-300 mt-1"
                                            />
                                            <label htmlFor={`addr-${addr.id}`} className="ml-3 block text-sm text-gray-700 cursor-pointer w-full">
                                                <span className="font-medium block">{addr.type || 'Home'} - {addr.name}</span>
                                                <span className="block text-gray-500">{addr.address}, {addr.city}, {addr.state} - {addr.pincode}</span>
                                            </label>
                                        </div>
                                    ))}
                                    {addresses.length === 0 && (
                                        <p className="text-gray-500 text-center">No other addresses found.</p>
                                    )}
                                </div>
                                <div className="flex justify-end gap-3">
                                    <button
                                        onClick={() => setAddressModalOpen(false)}
                                        className="px-4 py-2 border rounded-lg hover:bg-gray-50 text-gray-700"
                                    >
                                        Cancel
                                    </button>
                                    <button
                                        onClick={handleUpdateAddress}
                                        disabled={actionLoading || !selectedAddressId}
                                        className="px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 disabled:opacity-50"
                                    >
                                        {actionLoading ? 'Updating...' : 'Update Address'}
                                    </button>
                                </div>
                            </div>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
};

export default OrderTracking;
